import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BRAND, relativeTime } from '@fanvote25/shared';
import { useAuth } from '../lib/auth';
import { useMarkNotificationsRead, useNotifications, useWeeks } from '../lib/queries';
import { BRAND_LOGO, BRAND_MARK } from '../data/brandAssets';
import { Avatar } from './ui';
import './Layout.css';

/* ------------------------------------------------------------------ */
/* Brand mark                                                          */
/* ------------------------------------------------------------------ */

export function Logo({ compact = false }: { compact?: boolean }) {
  // A file dropped into web/src/assets/brand/ replaces the text wordmark.
  const image = compact ? BRAND_MARK : BRAND_LOGO;
  return (
    <Link to="/" className={`fv-logo-mark ${compact ? 'fv-logo-mark-compact' : ''}`} aria-label="FanVote25 home">
      {image ? (
        <img
          src={image}
          alt="FanVote25"
          className={`fv-brand-img ${compact ? 'fv-brand-img-compact' : ''}`}
        />
      ) : (
        <>
          <span className="fv-wordmark">
            FanVote<span className="fv-wordmark-25">25</span>
          </span>
          {!compact && <span className="fv-wordmark-tag">{BRAND.tagline}</span>}
        </>
      )}
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Navigation definitions                                              */
/* ------------------------------------------------------------------ */

const PRIMARY_NAV = [
  { to: '/', label: 'Home', icon: '⌂', end: true },
  { to: '/ballot', label: 'My Ballot', icon: '☑' },
  { to: '/poll', label: 'FanVote25 Poll', icon: '☰' },
  { to: '/scoreboard', label: 'Scoreboard', icon: '⊞' },
  { to: '/teams', label: 'Teams', icon: '⛉' },
  { to: '/voters', label: 'Voters', icon: '☺' },
  { to: '/history', label: 'History', icon: '↻' },
];

const MOBILE_NAV = [
  { to: '/', label: 'Home', icon: '⌂', end: true },
  { to: '/ballot', label: 'Ballot', icon: '☑' },
  { to: '/poll', label: 'Poll', icon: '☰' },
  { to: '/scoreboard', label: 'Scores', icon: '⊞' },
  { to: '/profile', label: 'Profile', icon: '☺' },
];

/* ------------------------------------------------------------------ */
/* Week selector                                                       */
/* ------------------------------------------------------------------ */

function WeekSelector() {
  const { data } = useWeeks();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const current = params.get('week') ?? String(data?.currentWeekNumber ?? '');

  if (!data) return <div className="fv-week-select fv-skeleton" style={{ width: 120, height: 38 }} />;

  return (
    <label className="fv-week-select">
      <span className="fv-sr-only">Select week</span>
      <span className="fv-week-icon" aria-hidden="true">
        &#128197;
      </span>
      <select
        value={current}
        onChange={(event) => {
          const next = new URLSearchParams(location.search);
          next.set('week', event.target.value);
          navigate(`${location.pathname}?${next.toString()}`);
        }}
      >
        {data.weeks.map((week) => (
          <option key={week.id} value={week.number}>
            Week {week.number}
            {week.number === data.currentWeekNumber ? ' (current)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------------------ */
/* Notifications                                                       */
/* ------------------------------------------------------------------ */

function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const { data } = useNotifications(Boolean(user));
  const markRead = useMarkNotificationsRead();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;
  const unread = data?.unread ?? 0;

  return (
    <div className="fv-bell-wrap" ref={ref}>
      <button
        type="button"
        className="fv-icon-btn"
        aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span aria-hidden="true">&#128276;</span>
        {unread > 0 && <span className="fv-bell-badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="fv-dropdown fv-dropdown-wide" role="menu">
          <div className="fv-dropdown-head">
            <strong>Notifications</strong>
            {unread > 0 && (
              <button
                type="button"
                className="fv-btn fv-btn-ghost fv-btn-sm"
                onClick={() => markRead.mutate(undefined)}
              >
                Mark all read
              </button>
            )}
          </div>
          <ul className="fv-notif-list">
            {(data?.notifications ?? []).slice(0, 12).map((notification) => (
              <li key={notification.id} className={notification.readAt ? '' : 'fv-notif-unread'}>
                <Link to={notification.link ?? '/'} onClick={() => setOpen(false)}>
                  <div className="fv-notif-title">{notification.title}</div>
                  <div className="fv-notif-body">{notification.body}</div>
                  <div className="fv-notif-time">{relativeTime(notification.createdAt)}</div>
                </Link>
              </li>
            ))}
            {(data?.notifications ?? []).length === 0 && (
              <li className="fv-notif-empty">Nothing yet. We will tell you when your ballot opens.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Profile menu                                                        */
/* ------------------------------------------------------------------ */

function ProfileMenu() {
  const { user, signOut, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) {
    return (
      <div className="fv-row" style={{ gap: 8 }}>
        <Link to="/signin" className="fv-btn fv-btn-sm">
          Sign In
        </Link>
        <Link to="/signup" className="fv-btn fv-btn-primary fv-btn-sm">
          Join
        </Link>
      </div>
    );
  }

  return (
    <div className="fv-profile-wrap" ref={ref}>
      <button
        type="button"
        className="fv-profile-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <Avatar name={`${user.firstName} ${user.lastName}`} color={user.avatarColor} size={30} />
        <span className="fv-hide-mobile fv-profile-name">
          {user.firstName} {user.lastName.charAt(0)}.
        </span>
        <span aria-hidden="true" className="fv-caret">
          &#9662;
        </span>
      </button>

      {open && (
        <div className="fv-dropdown" role="menu">
          <Link to="/profile" role="menuitem" onClick={() => setOpen(false)}>
            My Profile
          </Link>
          <Link to="/ballot" role="menuitem" onClick={() => setOpen(false)}>
            My Ballot
          </Link>
          <Link to="/profile?tab=history" role="menuitem" onClick={() => setOpen(false)}>
            Voting History
          </Link>
          <Link to="/settings" role="menuitem" onClick={() => setOpen(false)}>
            Settings
          </Link>
          {isAdmin && (
            <Link to="/admin" role="menuitem" onClick={() => setOpen(false)}>
              Admin Console
            </Link>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              signOut();
              setOpen(false);
              navigate('/');
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shell                                                               */
/* ------------------------------------------------------------------ */

export function TopNav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <header className="fv-topnav">
      <div className="fv-topnav-inner">
        <Logo />

        <nav className="fv-primary-nav" aria-label="Primary">
          {PRIMARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `fv-nav-item ${isActive ? 'fv-nav-item-active' : ''}`}
            >
              <span className="fv-nav-icon" aria-hidden="true">
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="fv-topnav-right">
          <div className="fv-hide-mobile">
            <WeekSelector />
          </div>
          <NotificationBell />
          <ProfileMenu />
          <button
            type="button"
            className="fv-icon-btn fv-only-mobile"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <span aria-hidden="true">&#9776;</span>
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="fv-mobile-menu" aria-label="All sections">
          {PRIMARY_NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className="fv-mobile-menu-item">
              <span aria-hidden="true">{item.icon}</span> {item.label}
            </NavLink>
          ))}
          <div className="fv-mobile-menu-week">
            <WeekSelector />
          </div>
        </nav>
      )}
    </header>
  );
}

export function BottomNav() {
  return (
    <nav className="fv-bottomnav" aria-label="Primary mobile">
      {MOBILE_NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) => `fv-bottomnav-item ${isActive ? 'fv-bottomnav-active' : ''}`}
        >
          <span className="fv-bottomnav-icon" aria-hidden="true">
            {item.icon}
          </span>
          <span className="fv-bottomnav-label">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="fv-footer">
      <div className="fv-footer-inner">
        <Logo compact />
        <p className="fv-muted">
          {BRAND.tagline} FanVote25 is a fan-run poll and is not affiliated with the NCAA or any
          member institution.
        </p>
        <nav className="fv-footer-links" aria-label="Footer">
          <Link to="/poll">Poll</Link>
          <Link to="/history">History</Link>
          <Link to="/voters">Voters</Link>
          <Link to="/how-it-works">How It Works</Link>
        </nav>
      </div>
    </footer>
  );
}
